import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { Pagination } from 'app/_models/pagination.model';
import { WorkerService } from 'app/_services/worker.service';
import { SortEvent } from 'primeng/api';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { environment } from 'environments/environment';
import { NotificationService } from 'app/_services/notification.service';
import { InviteUsersService } from 'app/_services/invite-users.service';
import { finalize } from 'rxjs/operators';
import { getStoredSiteOwnerSelection, setStoredSiteOwnerSelection } from 'app/_utils/site-owner-selection-storage.utils';

@Component({
  selector: 'worker-search',
  templateUrl: './worker-search.component.html'
})
export class WorkerSearchComponent implements OnInit {

  public role: any;
  private routeRole: any;

  // display
  public users = [];
  public columnOptionsSidebarVisible: boolean;
  private selectedUser: any;
  private siteOwnerId: any;
  public isFilterLoading: boolean = false;

  public togglableColumns = [
    { displayName: 'Contractor', default: false },
    { displayName: 'Activated', default: true },
  ];

  // table options
  public tableOptions = {
    visibleColumns: [],
  }

  //filter fields
  private email: string;
  private lastName: string;
  private firstName: string;
  private phoneNumber: string;
  private contractor: string;

  // pagination - reduced page sizes - since this could be a drain on performance
  public pageSizes: any = environment.perPageOptionsSmall
  public pagination: Pagination;
  @ViewChild('paginator', { static: true }) paginator;

  // Sorting and Filtering
  public defaultSortField = 'user.lastName';
  public defaultSortOrder = 1;
  private sortOrderToText = (sortOrder: number) => { return sortOrder === 1 ? 'asc' : 'desc'; }
  public selectedSort = [this.defaultSortField + ',' + this.sortOrderToText(this.defaultSortOrder)];

  // delegate with multiple site owners
  public selectedSiteOwner;
  public siteOwners = [];
  private isSiteOwner: boolean;
  private isSiteDelegate: boolean;
  private isAuthorisingOfficer: boolean;
  private siteOwnerIds: number[] = [];
  public showInviteUsersButton: boolean;

  // Skeleton loading
  public loading: boolean = true;

  constructor(
    private workerService: WorkerService,
    private router: Router,
    private globals: Globals,
    private activatedRoute: ActivatedRoute,
    private siteOwnerUserService: SiteOwnerUserService,
    private notificationService: NotificationService,
    private inviteUsersService: InviteUsersService,
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;
    this.isSiteOwner = this.routeRole === 'site-owner';
    this.isSiteDelegate = this.routeRole === 'site-delegate';
    this.isAuthorisingOfficer = this.routeRole === 'authorising-officer';
    this.showInviteUsersButton = !this.isAuthorisingOfficer;

    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedUser = parseInt(params.worker);
    });
  }

  ngOnInit(): void {
    this.setupColumnOptions();
    this.initializePagination();
    this.getSiteOwnerId()
      .then(() => this.resetUserList())
      .catch(() => this.resetUserList());
  }

  public getUserList() {
    this.loading = true;

    if (!this.siteOwnerId) {
      this.isFilterLoading = false;
      this.users = [];
      this.pagination.totalRecords = 0;
      this.loading = false;
      return;
    }

    this.workerService.getAllUsersAsSiteOwnerOrDelegate(this.siteOwnerId, this.email, this.firstName, this.lastName, this.phoneNumber, this.contractor, this.pagination, this.selectedSort)
    .pipe(finalize(() => setTimeout(() => { this.loading = false; }, 500)))
    .subscribe((data: any) => {
      this.isFilterLoading = false;
      this.users = data.body;
      this.pagination.totalRecords = Number(data.totalRecords);
    }, (error) => {
      this.isFilterLoading = false;
      console.error(error);
      this.notificationService.notify('danger', 'Error retrieving user list');
    });
  }

  public onRowSelect(event) {
    this.selectedUser = event.data;
    if (this.selectedUser) {
      const queryParams: any = { backLink: `/${this.routeRole}/workers` };
      if (this.siteOwnerId) {
        queryParams.siteOwnerId = this.siteOwnerId;
      }

      const workerDestination = this.isAuthorisingOfficer ? 'manage-competencies' : 'details';

      this.router.navigate(
        [`/${this.routeRole}/workers/${this.selectedUser.id}/${workerDestination}`],
        { queryParams }
      );
    }
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getUserList();
  }

  private initializePagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
  }

  private resetUserList() {
    this.goToFirstPage();
    this.getUserList();
  }

  public resetColumnOptions() {
    this.tableOptions = {
      visibleColumns: this.togglableColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.userSearch', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.userSearch');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }
  public onFilterTextChanged(filterText: string, filterField: string){
    this.isFilterLoading = true;
    if(filterField === 'fname'){
      this.firstName = filterText;
    } else if(filterField === 'lname'){
      this.lastName = filterText;
    } else if(filterField === 'email'){
      this.email = filterText;
    } else if(filterField === 'phoneNumber'){
      this.phoneNumber = filterText;
    } else if(filterField === 'contractor'){
      this.contractor = filterText;
    }

    this.goToFirstPage();
    this.getUserList();
  }

  public customSort(sort: SortEvent) {
    const sortDirection = this.sortOrderToText(sort.order);
    this.selectedSort = [sort.field + ',' + sortDirection];

    this.goToFirstPage();

    this.getUserList();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public selectedSiteOwnerChange() {
    if (!this.selectedSiteOwner || !this.selectedSiteOwner.id) {
      this.siteOwnerId = null;
      this.goToFirstPage();
      this.getUserList();
      return;
    }

    this.siteOwnerId = this.selectedSiteOwner.id;
    this.persistSelectedSiteOwner();
    this.goToFirstPage();
    this.getUserList();
  }

  private getSiteOwnerId(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isSiteOwner) {
        this.siteOwnerId = this.globals.siteOwnerUsers[0].skylarkUserId;
        resolve();
        return;
      }

      if (this.isSiteDelegate || this.isAuthorisingOfficer) {
        this.siteOwnerIds = this.globals.siteOwnerUsers.map(sou => sou.skylarkUserId);

        if (this.siteOwnerIds.length === 1) {
          this.siteOwnerId = this.siteOwnerIds[0];
          resolve();
          return;
        }

        // for users delegated by multiple site owners, need a dropdown to choose which site owner
        if (this.siteOwnerIds.length > 1) {
          this.siteOwnerId = null;
          this.selectedSiteOwner = null;

          this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().subscribe((data: any) => {
            this.siteOwners = data
              .filter(element => this.siteOwnerIds.includes(element.id))
              .map(element => ({
                label: element.firstName + ' ' + element.lastName,
                value: { id: element.id },
              }));

            const storedSiteOwnerId = this.getStoredSiteOwnerId();
            if (this.siteOwnerIds.includes(storedSiteOwnerId)) {
              this.selectedSiteOwner = { id: storedSiteOwnerId };
              this.siteOwnerId = storedSiteOwnerId;
            }

            resolve();
          }, error => {
            console.error(error);
            this.notificationService.notify('danger', 'Error retrieving site owner list');
            reject(error);
          });
          return;
        }
      }

      resolve();
    });
  }

  private getStoredSiteOwnerId(): number | null {
    return getStoredSiteOwnerSelection(this.globals.user.id);
  }

  private persistSelectedSiteOwner(): void {
    if (!this.siteOwnerId) {
      return;
    }

    setStoredSiteOwnerSelection(this.globals.user.id, this.siteOwnerId);
  }

  public resendInvite(user) {
    if (user.isSendingInvite || user.inviteSent) {
      return;
    }

    user.isSendingInvite = true;

    this.inviteUsersService.resendInvite(user.login).subscribe(
      (response) => {
        user.isSendingInvite = false;
        user.inviteSent = true;
        this.notificationService.notify('success', 'Invite resent to ' + user.email);
      },
      (error) => {
        user.isSendingInvite = false;
        this.notificationService.notify('danger', 'Error', 'Failed to resend invite to ' + user.email);
      }
    );
  }

  public goToInviteUsers() {
    this.router.navigate([`/${this.routeRole}/invite-users`]);
  }

  // Toggle dropdown using enter/space. Older version of primeNG doesn't support this, so we need to do it manually.
  public toggleDropdown(event: KeyboardEvent) {
    const dropdown = event.currentTarget as HTMLElement;
    const trigger = dropdown.querySelector('.p-dropdown-trigger') as HTMLElement;
    const panel = document.querySelector('.p-dropdown-panel') as HTMLElement;
    if (panel && panel.offsetParent !== null) {
      document.body.click(); // Close the dropdown
    }
    else if (trigger) {
      trigger.click(); // Open the dropdown
    }
    event.preventDefault();
  }
}
